import { ArrowRight, Code2, ShieldCheck, Users, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * AudienceCard — the hub-page picker that opens an audience subtree.
 *
 * Design plan reference,
 * section 22.1 (Home hero — three audience cards) and
 * section 22.6 (404 recovery — same three cards in compact form).
 *
 * Composes Card + Lucide icon + a "Browse" CTA. The entire card is a
 * single hyperlink — onClick fires on any click within the card body.
 *
 * Audience colours follow plan section 22.5 (Developer = info / cyan,
 * Tenant admin = success / green, CP admin = primary / navy). The
 * coloured tint is applied to the icon container only; the card body
 * stays neutral so a grid of three cards reads as a balanced set
 * rather than three different-coloured callouts.
 *
 * `highlighted` is the "you are here" state used on audience landing
 * pages — adds a brand-green border + soft glow so the user can see
 * which subtree they're currently in without re-reading every card.
 */

export type AudienceKind = 'developer' | 'tenant_admin' | 'cp_admin'

export interface AudienceCardProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'title'> {
  audience: AudienceKind
  /** Card title. Usually the audience display name. */
  title: string
  /** One-sentence description below the title. */
  description: string
  /** 3–4 short feature bullets. Lists with more than 4 entries look noisy. */
  bullets: string[]
  /** Target URL. The whole card is a single link. */
  href: string
  /** Optional icon override. Defaults map to a sensible Lucide icon per audience. */
  icon?: LucideIcon
  /** "You are here" treatment — brand-green border + glow. */
  highlighted?: boolean
  /** CTA label. Default 'Browse'. The arrow is decorative and always shown. */
  ctaLabel?: string
}

const DEFAULT_ICON: Record<AudienceKind, LucideIcon> = {
  developer: Code2,
  tenant_admin: Users,
  cp_admin: ShieldCheck,
}

/**
 * Icon tint per audience. Each maps to a soft tinted bg + a darker
 * foreground colour so the icon reads at a glance without dominating
 * the card. All three combinations pass WCAG AA contrast in light mode.
 */
const ICON_TINT: Record<AudienceKind, { bg: string; fg: string }> = {
  developer: { bg: 'bg-ia-cyan-muted', fg: 'text-ia-cyan' },
  tenant_admin: { bg: 'bg-ia-green-muted', fg: 'text-ia-green-text' },
  cp_admin: { bg: 'bg-ia-indigo-muted', fg: 'text-ia-indigo-text' },
}

export const AudienceCard = React.forwardRef<HTMLAnchorElement, AudienceCardProps>(
  (
    {
      audience,
      title,
      description,
      bullets,
      href,
      icon,
      highlighted = false,
      ctaLabel = 'Browse',
      className,
      ...rest
    },
    ref,
  ) => {
    const Icon = icon ?? DEFAULT_ICON[audience]
    const tint = ICON_TINT[audience]

    return (
      <a
        ref={ref}
        href={href}
        className={cn(
          // Container is a full Card visually, but rendered as a single <a>
          // so the whole surface is one click target (no nested-interactive
          // a11y trap).
          'group block rounded-lg border bg-card p-6 text-left',
          'transition-[transform,border-color,box-shadow] duration-150 ease-out motion-reduce:transition-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
          highlighted
            ? [
                'border-[var(--color-ia-sidebar-active-border)]',
                'shadow-[0_0_0_3px_var(--color-ia-sidebar-active-glow)]',
              ].join(' ')
            : [
                'border-ia-border',
                // Lift + border-recolor on hover. The CTA arrow nudges right
                // in tandem via group-hover (below).
                'hover:-translate-y-[2px] hover:border-[var(--color-ia-sidebar-active-border)]',
              ].join(' '),
          className,
        )}
        {...rest}
      >
        <div
          className={cn(
            'mb-4 inline-flex items-center justify-center h-10 w-10 rounded-md',
            tint.bg,
          )}
        >
          <Icon size={20} className={cn(tint.fg)} />
        </div>

        <h3 className="text-base font-semibold text-ia-text mb-1">{title}</h3>
        <p className="text-body-sm text-ia-text-secondary mb-4">{description}</p>

        <ul className="flex flex-col gap-1 mb-5 list-none p-0 m-0">
          {bullets.map((bullet) => (
            <li
              key={bullet}
              className="text-body-sm text-ia-text-secondary flex items-start gap-2"
            >
              <span
                aria-hidden="true"
                className="mt-[7px] inline-block h-[3px] w-[3px] rounded-pill bg-ia-text-tertiary shrink-0"
              />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>

        <div className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-ia-green-text whitespace-nowrap">
          {ctaLabel}
          <ArrowRight
            size={14}
            className="transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none"
          />
        </div>
      </a>
    )
  },
)
AudienceCard.displayName = 'AudienceCard'
